import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription, SUBSCRIPTION_TIERS } from "@/hooks/useSubscription";
import { toast } from "sonner";

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribed, tier, createCheckout, openCustomerPortal, loading: subLoading } = useSubscription();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const plans = [
    {
      key: "basic" as const,
      name: "Basic",
      price: "$9.99",
      period: "/month",
      description: "Essential features for getting started",
      features: [
        "50 thumbnail generations/month",
        "HD export quality",
        "All style presets",
        "No watermark",
        "Email support",
      ],
      popular: false,
    },
    {
      key: "pro" as const,
      name: "Pro",
      price: "$29.99",
      period: "/month",
      description: "For serious content creators",
      features: [
        "Unlimited thumbnail generations",
        "HD & 4K export",
        "All style presets & emotions",
        "Priority generation",
        "A/B testing variations",
        "Brand kit storage",
        "Priority support",
      ],
      popular: true,
    },
    {
      key: "enterprise" as const,
      name: "Enterprise",
      price: "$99.99",
      period: "/month",
      description: "For teams and agencies",
      features: [
        "Everything in Pro",
        "5 team members",
        "Unlimited brand kits",
        "Bulk generation (50+ at once)",
        "API access",
        "White-label exports",
        "Dedicated account manager",
        "Custom integrations",
      ],
      popular: false,
    },
  ];

  const handleSubscribe = async (planKey: keyof typeof SUBSCRIPTION_TIERS) => {
    if (!user) {
      navigate("/signup");
      return;
    }

    // If already subscribed to this plan, open customer portal
    if (subscribed && tier === planKey) {
      try {
        setLoadingPlan(planKey);
        await openCustomerPortal();
      } catch (error) {
        toast.error("Failed to open subscription management");
      } finally {
        setLoadingPlan(null);
      }
      return;
    }

    try {
      setLoadingPlan(planKey);
      const priceId = SUBSCRIPTION_TIERS[planKey].price_id;
      await createCheckout(priceId);
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoadingPlan(null);
    }
  };

  const getButtonText = (planKey: keyof typeof SUBSCRIPTION_TIERS) => {
    if (loadingPlan === planKey) return "Loading...";
    if (subscribed && tier === planKey) return "Manage Plan";
    if (subscribed) return "Switch Plan";
    return "Get Started";
  };

  const isCurrentPlan = (planKey: keyof typeof SUBSCRIPTION_TIERS) => {
    return subscribed && tier === planKey;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="section-padding relative overflow-hidden">
          <div className="absolute inset-0 hero-glow" />
          <div className="container-custom relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <span className="text-sm font-semibold text-brand-purple uppercase tracking-wider mb-4 block">
                Pricing
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Simple, Transparent{" "}
                <span className="gradient-text">Pricing</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Choose the plan that fits your needs. Upgrade or downgrade anytime.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20">
          <div className="container-custom">
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {plans.map((plan) => (
                <div
                  key={plan.key}
                  className={`relative p-8 rounded-2xl border transition-all duration-300 ${
                    isCurrentPlan(plan.key)
                      ? "bg-gradient-to-b from-green-500/10 to-card border-green-500/50 shadow-2xl"
                      : plan.popular
                      ? "bg-gradient-to-b from-primary/10 to-card border-primary/50 scale-105 shadow-2xl"
                      : "bg-card border-border hover:border-primary/30"
                  }`}
                >
                  {isCurrentPlan(plan.key) && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <div className="px-4 py-1 rounded-full bg-green-500 text-sm font-semibold text-white flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        Your Plan
                      </div>
                    </div>
                  )}
                  {!isCurrentPlan(plan.key) && plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <div className="px-4 py-1 rounded-full gradient-bg text-sm font-semibold text-primary-foreground flex items-center gap-1">
                        <Sparkles className="w-4 h-4" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold gradient-text">{plan.price}</span>
                      <span className="text-muted-foreground">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground/90">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={isCurrentPlan(plan.key) ? "outline" : plan.popular ? "hero" : "outline"}
                    className="w-full"
                    size="lg"
                    onClick={() => handleSubscribe(plan.key)}
                    disabled={loadingPlan !== null || subLoading}
                  >
                    {loadingPlan === plan.key ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        {getButtonText(plan.key)}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Preview */}
        <section className="section-padding bg-card/50">
          <div className="container-custom">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Have Questions?
              </h2>
              <p className="text-muted-foreground mb-8">
                Check out our FAQ for answers to common questions.
              </p>
              <Link to="/faq">
                <Button variant="outline" size="lg">
                  View FAQ
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
